import clsx from "clsx";
import Icon, {type IconNameExtended} from "@/components/library/Icon";
import Tooltip from "@/components/library/Tooltip";

export interface SegmentedControlOption<T extends string> {
   key: T;
   label: string;
   icon?: IconNameExtended;
   /** Disables the option and applies muted styling. */
   disabled?: boolean;
   /** Optional tooltip shown on hover/focus. */
   tooltip?: React.ReactNode;
   dataTestId?: string;
}

interface SegmentedControlProps<T extends string> {
   options: SegmentedControlOption<T>[];
   value: T;
   onChange: (value: T) => void;
   className?: string;
   /** Accessible label for the radiogroup. */
   ariaLabel?: string;
   /** When true, the control fills its container and each option has equal width. */
   fullWidth?: boolean;
}

export default function SegmentedControl<T extends string>({
   options,
   value,
   onChange,
   className,
   ariaLabel,
   fullWidth = false,
}: Readonly<SegmentedControlProps<T>>) {
   return (
      <div
         role="radiogroup"
         aria-label={ariaLabel}
         className={clsx(
            "rounded-xl border border-neutral bg-panel_secondary/30 p-1 gap-1",
            fullWidth ? "flex w-full" : "inline-flex",
            className
         )}
      >
         {options.map((option) => {
            const isActive = value === option.key;
            const button = (
               <button
                  key={option.key}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  aria-disabled={option.disabled}
                  disabled={option.disabled}
                  onClick={() => !option.disabled && onChange(option.key)}
                  data-testid={option.dataTestId}
                  className={clsx(
                     "flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap",
                     "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                     fullWidth && "flex-1",
                     option.disabled
                        ? "text-400 cursor-not-allowed"
                        : isActive
                           ? "bg-primary/10 text-primary shadow-sm"
                           : "text-600 hover:text-800 hover:bg-300/30"
                  )}
               >
                  {option.icon && <Icon icon={option.icon} className="text-xs" />}
                  {option.label}
               </button>
            );

            if (option.tooltip) {
               // Wrap disabled buttons in a span so the tooltip can still
               // capture hover/focus events (disabled <button> elements
               // suppress pointer events in most browsers).
               const tooltipChild = option.disabled ? (
                  <span className="inline-flex">{button}</span>
               ) : (
                  button
               );
               return (
                  <Tooltip key={option.key} content={option.tooltip}>
                     {tooltipChild}
                  </Tooltip>
               );
            }

            return button;
         })}
      </div>
   );
}
